import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import type {
  ProfileViewUser,
  ProjectViewUser,
} from '@freelancer/datastore/collections';
import { Location, Review, User } from '@freelancer/datastore/collections';
import { ButtonColor } from '@freelancer/ui/button';
import { trackById } from '@freelancer/ui/helpers';
import {
  IconBackdrop,
  IconBackdropSize,
  IconBackdropStyle,
  IconColor,
  IconSize,
} from '@freelancer/ui/icon';
import { LinkColor } from '@freelancer/ui/link';
import { PictureObjectFit } from '@freelancer/ui/picture';
import { TagSize } from '@freelancer/ui/tag';
import {
  FontColor,
  FontStyle,
  FontType,
  FontWeight,
  TextAlign,
  TextSize,
} from '@freelancer/ui/text';
import {
  AvatarSize,
  type User as UserAvatar,
} from '@freelancer/ui/user-avatar';
import { PriceTierApi } from 'api-typings/projects/projects';

enum Perspective {
  REVIEWER = 'REVIEWER',
  REVIEWEE = 'REVIEWEE',
  THIRD_PARTY = 'THIRD_PARTY',
  LOGGED_OUT = 'LOGGED_OUT',
}

@Component({
  selector: 'fl-review-card-reply',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewCardReplyComponent {}

@Component({
  selector: 'fl-review-card-services',
  template: `
    <div class="Container">
      <fl-picture
        i18n-alt="Service image alt text"
        alt="Service image"
        class="ServiceImg"
        [src]="imgSrc"
        [aspectRatioValue]="'rectangle-medium'"
        [objectFit]="PictureObjectFit.COVER"
        [alignCenter]="true"
        [borderRadiusBottom]="true"
        [borderRadiusTop]="true"
      ></fl-picture>
      <div class="Tag">
        <p
          class="text-color-inherit text-mid tablet:text-xsmall font-medium"
          i18n="Service tag"
        >
          {{ serviceTag }}
        </p>
      </div>
      <div class="ServiceLinkContainer">
        <fl-button
          i18n="View service button"
          class="ServiceLink"
          [display]="'flex'"
          [color]="ButtonColor.PRIMARY"
          [link]="serviceLink"
        >
          View service
        </fl-button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./review-card-services.component.scss'],
})
export class ReviewCardServicesComponent {
  ButtonColor = ButtonColor;
  FontColor = FontColor;
  FontWeight = FontWeight;
  TextSize = TextSize;
  PictureObjectFit = PictureObjectFit;

  @Input() imgSrc: string;
  @Input() serviceTag: string;
  @Input() serviceLink: string;
}

@Component({
  selector: 'fl-review-card',
  template: `
    <div
      class="MainContainer"
      flTrackingSection="ReviewItem"
    >
      <div
        class="Container"
        [class.ContainerFix]="reviewCardServicesComponent"
      >
        <fl-user-avatar
          [backgroundColor]="true"
          [flMarginRight]="'small'"
          [flMarginBottom]="'xxsmall'"
          [size]="AvatarSize.SMALL"
          [sizeTablet]="AvatarSize.XLARGE"
          [users]="[reviewerAvatar]"
        ></fl-user-avatar>
        <div class="Body">
          <fl-rating
            class="Rating"
            [flMarginBottom]="'xxsmall'"
            [control]="reputationControl"
            [hideValue]="true"
            [readOnly]="true"
            [size]="IconSize.LARGE"
          ></fl-rating>

          <fl-text
            [flMarginBottom]="'xxsmall'"
            [fontType]="FontType.PARAGRAPH"
            [size]="size"
            [sizeDesktop]="sizeDesktop"
            [sizeDesktopXXLarge]="sizeDesktopXXLarge"
            [weight]="FontWeight.MEDIUM"
          >
            <fl-link
              flTrackingLabel="ReviewCardContextLink"
              [flMarginRight]="'xxxsmall'"
              [color]="LinkColor.INHERIT"
              [link]="review.context.seoUrl"
              [size]="TextSize.INHERIT"
              >{{ review.context.name }}</fl-link
            >
            <span [flMarginRight]="'xxxsmall'">
              {{ '•' }}
            </span>
            <span>
              {{
                hidePrice
                  ? priceTier
                  : (cost
                    | flCurrency
                      : review.currency.code
                      : { withPrecision: false })
              }}
            </span>
          </fl-text>
          <fl-text
            [fontType]="FontType.PARAGRAPH"
            [flMarginBottom]="'xxsmall'"
            [size]="size"
            [sizeDesktop]="sizeDesktop"
            [sizeDesktopXXLarge]="sizeDesktopXXLarge"
          >
            {{ review.description }}
          </fl-text>
          <div
            *ngIf="review.skills"
            [flHideMobile]="true"
            [flMarginBottom]="'xxsmall'"
          >
            <div>
              <ng-container
                *ngFor="
                  let skill of review.skills | slice: 0 : displayLimit;
                  trackBy: trackById
                "
              >
                <fl-tag
                  *ngIf="skill.name !== 'Hire me'"
                  flTrackingLabel="SkillLink"
                  flTrackingReferenceType="skill_id"
                  flTrackingReferenceId="{{ skill.id }}"
                  [borderless]="true"
                  [link]="'/freelancers/skills/' + skill.seoUrl"
                  [sizeDesktopXXLarge]="tagSizeDesktopXXLarge"
                >
                  {{ skill.name }}
                </fl-tag>
              </ng-container>
              <fl-text
                *ngIf="review.skills.length > displayLimit"
                class="ExtraSkills"
                i18n="Number of extra skills"
                [size]="size"
                [sizeDesktop]="sizeDesktop"
                [sizeDesktopXXLarge]="sizeDesktopXXLarge"
              >
                +{{ review.skills.length - displayLimit }} more
              </fl-text>
            </div>
          </div>
          <div
            *ngIf="reviewer && !reviewer.closed; else closedUser"
            class="InfoContainer"
            [flMarginBottom]="'xxxsmall'"
          >
            <fl-text
              [flMarginBottom]="'xxxsmall'"
              [flMarginRight]="'xxsmall'"
              [size]="size"
              [sizeDesktop]="sizeDesktop"
              [sizeDesktopXXLarge]="sizeDesktopXXLarge"
              [textAlign]="TextAlign.LEFT"
              [weight]="FontWeight.MEDIUM"
            >
              {{ reviewer.displayName }}
            </fl-text>
            <fl-text
              [flMarginBottom]="'xxxsmall'"
              [flMarginRight]="'xxxsmall'"
              [size]="size"
              [sizeDesktop]="sizeDesktop"
              [sizeDesktopXXLarge]="sizeDesktopXXLarge"
              [textAlign]="TextAlign.LEFT"
              [weight]="FontWeight.NORMAL"
              >&#64;{{ reviewer.username }}
            </fl-text>
            <fl-text
              [flMarginBottom]="'xxxsmall'"
              [flMarginRight]="'xxsmall'"
              [size]="size"
              [sizeDesktop]="sizeDesktop"
              [sizeDesktopXXLarge]="sizeDesktopXXLarge"
            >
              {{ '•' }}
            </fl-text>
            <fl-flag
              [countryCode]="reviewerLocation.country?.code"
              [flMarginBottom]="'xxxsmall'"
              [flMarginRight]="'xxsmall'"
            ></fl-flag>
            <fl-text
              i18n="City"
              [flMarginBottom]="'xxxsmall'"
              [flMarginRight]="'xxsmall'"
              [size]="size"
              [sizeDesktop]="sizeDesktop"
              [sizeDesktopXXLarge]="sizeDesktopXXLarge"
            >
              {{ reviewerLocation.city }}, {{ reviewerLocation.country?.name }}
            </fl-text>
            <fl-text
              [flMarginBottom]="'xxxsmall'"
              [flMarginRight]="'xxsmall'"
              [size]="size"
              [sizeDesktop]="sizeDesktop"
              [sizeDesktopXXLarge]="sizeDesktopXXLarge"
            >
              {{ '•' }}
            </fl-text>
            <fl-relative-time
              class="ReviewItem-time"
              [flMarginBottom]="'xxxsmall'"
              [date]="review.timeSubmitted"
              [size]="size"
              [sizeDesktop]="sizeDesktop"
              [sizeDesktopXXLarge]="sizeDesktopXXLarge"
              [strict]="true"
              [live]="false"
            ></fl-relative-time>
          </div>
          <ng-template #closedUser>
            <fl-text
              i18n="Former user display name"
              [flMarginBottom]="'xxxsmall'"
              [size]="size"
              [sizeDesktop]="sizeDesktop"
              [sizeDesktopXXLarge]="sizeDesktopXXLarge"
            >
              Former Client (inactive)
            </fl-text>
          </ng-template>
          <div
            class="ActionContainer"
            [flMarginBottom]="reviewCardReplyComponent ? 'small' : 'none'"
          >
            <div class="ActionContainer-block">
              <ng-container *ngIf="perspective === Perspective.THIRD_PARTY">
                <div
                  *ngIf="!hideHelpful"
                  tabindex="0"
                  class="IconContainer"
                  [flMarginRight]="'xxsmall'"
                  (click)="triggerHelpfulReaction()"
                >
                  <fl-icon
                    [flMarginRight]="'xxxsmall'"
                    [name]="'ui-thumb-up'"
                    [useIconFont]="true"
                    [fillIconFont]="helpfulReaction"
                    [color]="
                      helpfulReaction
                        ? IconColor.SECONDARY
                        : IconColor.FOREGROUND
                    "
                    [size]="IconSize.XSMALL"
                  ></fl-icon>
                  <fl-text
                    i18n="Helpful"
                    [size]="size"
                    [sizeDesktop]="sizeDesktop"
                    [sizeDesktopXXLarge]="sizeDesktopXXLarge"
                    [textDecoration]="'underline'"
                  >
                    Helpful
                  </fl-text>
                </div>
              </ng-container>
              <ng-container *ngIf="perspective === Perspective.REVIEWEE">
                <div
                  *ngIf="!hideReply"
                  class="IconContainer"
                  tabindex="0"
                  [flMarginRight]="'xxsmall'"
                  (click)="triggerReply()"
                >
                  <fl-icon
                    [flMarginRight]="'xxxsmall'"
                    [name]="'ui-comment'"
                    [useIconFont]="true"
                    [size]="IconSize.XSMALL"
                  ></fl-icon>
                  <fl-text
                    i18n="Reply"
                    [size]="size"
                    [sizeDesktop]="sizeDesktop"
                    [sizeDesktopXXLarge]="sizeDesktopXXLarge"
                    [textDecoration]="'underline'"
                  >
                    Reply
                  </fl-text>
                </div>
                <div
                  *ngIf="!!reviewee.corporate"
                  class="IconContainer"
                  tabindex="0"
                  [flMarginRight]="'xxsmall'"
                  (click)="triggerFeature()"
                >
                  <fl-icon
                    [flMarginRight]="'xxxsmall'"
                    [name]="'ui-star'"
                    [useIconFont]="true"
                    [fillIconFont]="review.featured"
                    [color]="
                      review.featured
                        ? IconColor.SECONDARY
                        : IconColor.FOREGROUND
                    "
                    [size]="IconSize.XSMALL"
                  ></fl-icon>
                  <fl-text
                    i18n="Toggle feature action cta"
                    [size]="size"
                    [sizeDesktop]="sizeDesktop"
                    [sizeDesktopXXLarge]="sizeDesktopXXLarge"
                    [textDecoration]="'underline'"
                  >
                    {{ review.featured ? 'Remove featured' : 'Feature' }}
                  </fl-text>
                </div>
              </ng-container>
              <div
                class="IconContainer"
                [flMarginRight]="'xxsmall'"
                tabindex="0"
                [flShowMobile]="!reviewCardServicesComponent"
                (click)="triggerShare()"
              >
                <fl-icon
                  [flMarginRight]="'xxxsmall'"
                  [name]="'ui-share'"
                  [useIconFont]="true"
                  [size]="IconSize.XSMALL"
                ></fl-icon>
                <fl-text
                  i18n="Share"
                  [size]="size"
                  [sizeDesktop]="sizeDesktop"
                  [sizeDesktopXXLarge]="sizeDesktopXXLarge"
                  [textDecoration]="'underline'"
                >
                  Share
                </fl-text>
              </div>
            </div>
            <!-- Review author (the reviewer) can't report their own review -->
            <div
              *ngIf="
                !hideReport &&
                (perspective === Perspective.REVIEWEE ||
                  perspective === Perspective.THIRD_PARTY)
              "
              class="IconContainer"
              tabindex="0"
              (click)="triggerReport()"
            >
              <fl-icon
                [flMarginRight]="'xxxsmall'"
                [name]="'ui-flag'"
                [useIconFont]="true"
                [size]="IconSize.XSMALL"
              ></fl-icon>
              <fl-text
                i18n="Report"
                [size]="size"
                [sizeDesktop]="sizeDesktop"
                [sizeDesktopXXLarge]="sizeDesktopXXLarge"
                [textDecoration]="'underline'"
              >
                Report
              </fl-text>
            </div>
          </div>
          <div
            class="ReplyContainer"
            *ngIf="reviewCardReplyComponent"
          >
            <ng-content select="fl-review-card-reply"></ng-content>
          </div>
        </div>
        <fl-icon
          *ngIf="!reviewCardServicesComponent"
          class="Share"
          i18n-label="Share review button"
          label="Share"
          [name]="'ui-share-v3'"
          [color]="IconColor.FOREGROUND"
          [size]="IconSize.MID"
          [backdrop]="IconBackdrop.TRANSPARENT"
          [backdropStyle]="IconBackdropStyle.TRANSPARENT"
          [backdropSize]="IconbackdropSize.XXXSMALL"
          [clickable]="true"
          [flHideMobile]="true"
          (click)="triggerShare()"
        ></fl-icon>
      </div>
      <div
        *ngIf="reviewCardServicesComponent"
        [flHideMobile]="true"
      >
        <ng-content select="fl-review-card-services"></ng-content>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./review-card.component.scss'],
})
export class ReviewCardComponent implements OnChanges {
  AvatarSize = AvatarSize;
  FontColor = FontColor;
  FontType = FontType;
  FontWeight = FontWeight;
  FontStyle = FontStyle;
  IconColor = IconColor;
  IconbackdropSize = IconBackdropSize;
  IconBackdrop = IconBackdrop;
  IconBackdropStyle = IconBackdropStyle;
  IconSize = IconSize;
  LinkColor = LinkColor;
  Perspective = Perspective;
  TagSize = TagSize;
  TextAlign = TextAlign;
  TextSize = TextSize;
  trackById = trackById;

  reviewerAvatar: UserAvatar;
  reputationControl = new FormControl(0, { nonNullable: true });
  readonly displayLimit = 5;
  helpfulReaction = false;

  cost: number;
  hidePrice = false;
  perspective: Perspective;

  @Input() hideHelpful = false;
  @Input() hideReply = false;
  @Input() hideReport = false;
  @Input() loggedInUser: User;
  @Input() reviewee: User | ProfileViewUser;
  @Input() reviewer: User | ProjectViewUser;
  @Input() reviewerLocation: Location;
  @Input() review: Review;
  @Input() size = TextSize.SMALL;
  @Input() sizeDesktop?: TextSize;
  @Input() sizeDesktopXXLarge?: TextSize;
  @Input() tagSizeDesktopXXLarge: TagSize = TagSize.LARGE;

  @Output() clickHelpful = new EventEmitter<void>();
  @Output() clickReply = new EventEmitter<void>();
  @Output() clickReport = new EventEmitter<void>();
  @Output() clickShare = new EventEmitter<void>();
  @Output() clickFeature = new EventEmitter<void>();

  @ContentChild(ReviewCardReplyComponent)
  reviewCardReplyComponent: ReviewCardReplyComponent;

  @ContentChild(ReviewCardServicesComponent)
  reviewCardServicesComponent: ReviewCardServicesComponent;

  get priceTier(): string | undefined {
    switch (this.review.priceTier) {
      case PriceTierApi.LOW_PRICE:
        return '$';
      case PriceTierApi.MID_PRICE:
        return '$$';
      case PriceTierApi.HIGH_PRICE:
        return '$$$';
      case PriceTierApi.ULTRA_HIGH_PRICE:
        return '$$$$';
      default:
        return undefined;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.reviewer && 'reviewer' in changes) {
      this.reviewerAvatar = {
        avatar: this.reviewer.avatar,
        username: this.reviewer.username,
      };
    }

    if (
      'reviewer' in changes ||
      'reviewee' in changes ||
      'loggedInUser' in changes
    ) {
      if (
        this.loggedInUser &&
        this.reviewer &&
        this.loggedInUser.id === this.reviewer.id
      ) {
        this.perspective = Perspective.REVIEWER;
      } else if (
        this.loggedInUser &&
        this.reviewee &&
        this.loggedInUser.id === this.reviewee.id
      ) {
        this.perspective = Perspective.REVIEWEE;
      } else if (this.loggedInUser) {
        this.perspective = Perspective.THIRD_PARTY;
      } else {
        this.perspective = Perspective.LOGGED_OUT;
      }
    }

    if (this.review && 'review' in changes) {
      this.reputationControl = new FormControl(this.review.rating, {
        nonNullable: true,
      });

      // If the review is sealed and the logged in user is not the reviewer or reviewee, show price undisclosed
      if (
        this.review.sealed &&
        (!this.loggedInUser ||
          (this.review.fromUserId !== this.loggedInUser.id &&
            this.review.toUserId !== this.loggedInUser.id))
      ) {
        this.hidePrice = true;
      } else {
        this.cost = this.review.paidAmount || this.review.bidAmount;
      }
    }
  }

  triggerHelpfulReaction(): void {
    // Toggle button
    this.helpfulReaction = !this.helpfulReaction;

    this.clickHelpful.emit();
  }

  triggerReply(): void {
    this.clickReply.emit();
  }

  triggerReport(): void {
    this.clickReport.emit();
  }

  triggerShare(): void {
    this.clickShare.emit();
  }

  triggerFeature(): void {
    this.clickFeature.emit();
  }
}
